/**
 * Resize-handle helper for the editor's side panels. Each panel keeps
 * its own width in localStorage (key e.g. `ae.panelWidth.<id>`) and
 * exposes a thin DOM stripe the user can drag to widen / narrow.
 *
 * Two variants:
 * - `side: "right"` — handle sits on the panel's RIGHT edge, used for
 *   left-side panels (TypePalette, ConceptExplorer, RelationsPanel,
 *   DiagramsPanel). Drag right = wider.
 * - `side: "left"` — handle sits on the panel's LEFT edge, used for
 *   the right-side AttributePanel. Drag left = wider.
 *
 * Width is bounded between 160 and 720 px to keep the layout sane on
 * tiny windows and prevent a single panel from devouring the canvas.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const MIN_WIDTH = 160;
const MAX_WIDTH = 720;

export function usePanelWidth(
  storageKey: string,
  defaultWidth: number,
  side: "left" | "right",
): { width: number; ResizeHandle: () => React.ReactElement } {
  const { t } = useTranslation();
  const [width, setWidth] = useState<number>(() => readWidth(storageKey) ?? defaultWidth);
  // Mirror the latest width into a ref so the mouse-up handler reads
  // the final value when persisting. Without this, a stale closure
  // would write the width as it was at drag-start.
  const widthRef = useRef(width);
  widthRef.current = width;

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      // Capture pointer so the drag continues even if the cursor
      // momentarily leaves the handle's bounds.
      (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
      const startX = event.clientX;
      const startWidth = widthRef.current;
      const factor = side === "right" ? 1 : -1;
      const onMove = (m: PointerEvent) => {
        const next = clamp(startWidth + factor * (m.clientX - startX), MIN_WIDTH, MAX_WIDTH);
        setWidth(next);
      };
      const onUp = (m: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        const final = clamp(startWidth + factor * (m.clientX - startX), MIN_WIDTH, MAX_WIDTH);
        widthRef.current = final;
        writeWidth(storageKey, final);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [side, storageKey],
  );

  /** Dubbelklik op de handle → reset naar default. Handig wanneer een
   *  panel per ongeluk veel te breed is geworden of als de gebruiker
   *  wil terug naar de uitgangswaarde zonder pixel-by-pixel te slepen. */
  const onDoubleClick = useCallback(() => {
    widthRef.current = defaultWidth;
    setWidth(defaultWidth);
    writeWidth(storageKey, defaultWidth);
  }, [defaultWidth, storageKey]);

  // Persist on unmount as a safety net — covers the edge case where
  // the user changes width then immediately closes the tab without a
  // pointerup fire.
  useEffect(() => {
    return () => {
      writeWidth(storageKey, widthRef.current);
    };
  }, [storageKey]);

  const ResizeHandle = useCallback(() => {
    const positionClass = side === "right" ? "right-0" : "left-0";
    return (
      <div
        // Tiny invisible-ish drag stripe right on the panel edge. Cursor
        // hint + a subtle hover tint communicate it's interactive.
        className={`absolute top-0 z-10 h-full w-1.5 cursor-col-resize select-none hover:bg-blue-300/40 ${positionClass}`}
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        title={t("app.panels.resizeHandleTitle")}
        // biome-ignore lint/a11y/useAriaPropsForRole: pointer-only resize-splitter zonder keyboard-focus of numerieke breedte-waarde; aria-valuenow zou een misleidende statische waarde vereisen
        role="separator"
        aria-orientation="vertical"
      />
    );
  }, [side, onPointerDown, onDoubleClick, t]);

  return { width, ResizeHandle };
}

// ---- localStorage helpers --------------------------------------------------

function readWidth(key: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return null;
    return clamp(n, MIN_WIDTH, MAX_WIDTH);
  } catch {
    return null;
  }
}

function writeWidth(key: string, width: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(Math.round(width)));
  } catch {
    // ignore (private mode, quota)
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
